// =============================================================================
// ReceiptsRegisterScreen — /ap/register  (T-455)
// Director/manager view of EVERY loaded receipt: who loaded it, where it is in
// the flow (in queue → coded → in Xero → paid, or rejected/cancelled), and any
// staff reimbursement owed / paid back.
//
// Built after the AP audit (2026-08-04): the crew's receipts were all showing
// "loaded by James" (root cause fixed in migration 260) and there was no single
// place to sanity-check what everyone has submitted or track money owed back to
// the guys. RLS already lets manager+ read every pending_receipts row, so this
// is purely a read/reporting surface.
//
// 20 Sep 2026 (James): "how can I see the file rejected or get more details?"
// — a rejected Heim Property rental receipt needed to come back once the
// rental-ledger decision landed on "keep doing it through AP". Each row now
// expands: the receipt file itself (image / PDF, signed URL), the decline
// reason, notes, payment method, project, OCR guess — and a rejected one can
// be put back in the queue (status → new, reason cleared). Manager+ may update
// any pending_receipts row (policy pending_receipts_update), so no migration.
// =============================================================================

import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, ReceiptText, Search, AlertTriangle, Download, ExternalLink, RotateCcw, ChevronDown, ChevronUp,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Button, EmptyState, Spinner, Pill } from "@/components/ui";
import type { Tone } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";
import { exportCsv, dated } from "@/lib/exportCsv";

type PrStatus = "new" | "promoted" | "rejected" | "cancelled";

interface ApEmbed {
  ap_invoice_id: string;
  supplier_name: string | null;
  amount_inc_gst: number | null;
  status: string | null;
  is_reimbursement: boolean | null;
  reimburse_employee_id: string | null;
}
interface Row {
  receipt_id: string;
  captured_at: string | null;
  status: PrStatus;
  supplier_guess: string | null;
  amount_guess: number | null;
  notes: string | null;
  storage_path: string | null;
  mime_type: string | null;
  rejection_reason: string | null;
  payment_method: string | null;
  claimed_personal: boolean | null;
  project: { project_code: string; project_name: string } | null;
  captured_by_employee_id: string | null;
  capturer: { first_name: string | null; last_name: string | null } | null;
  promoted_to_ap_invoice_id: string | null;
  ap: ApEmbed | null;
  _reimbName?: string | null;
}

function loaderName(r: Row): string {
  const n = [r.capturer?.first_name, r.capturer?.last_name].filter(Boolean).join(" ");
  return n || "—";
}
function supplierOf(r: Row): string {
  return r.ap?.supplier_name || r.supplier_guess || "—";
}
function amountOf(r: Row): number | null {
  return r.ap?.amount_inc_gst ?? r.amount_guess ?? null;
}
function stage(r: Row): { label: string; tone: Tone } {
  if (r.status === "rejected")  return { label: "Rejected",  tone: "rejected" };
  if (r.status === "cancelled") return { label: "Cancelled", tone: "default" };
  if (r.status === "new")       return { label: "In queue",  tone: "pending" };
  const s = r.ap?.status;
  if (s === "paid")            return { label: "Paid",           tone: "ok" };
  if (s === "entered_in_xero") return { label: "In Xero",        tone: "notice" };
  if (s === "approved")        return { label: "Ready for Xero", tone: "notice" };
  if (s === "void")            return { label: "Voided",         tone: "default" };
  return { label: "Coded", tone: "notice" };
}
function reimb(r: Row): { label: string; tone: Tone; owedAmt: number } | null {
  if (!r.ap?.is_reimbursement) return null;
  const amt = r.ap.amount_inc_gst ?? 0;
  const who = r._reimbName || "staff";
  if (r.ap.status === "paid") return { label: `Paid back · ${who}`, tone: "ok", owedAmt: 0 };
  return { label: `Owed · ${who}`, tone: "warning", owedAmt: amt };
}

export default function ReceiptsRegisterScreen() {
  const user = useRequireAuth();
  const [q, setQ]           = useState("");
  const [status, setStatus] = useState<"all" | PrStatus>("all");
  const [loader, setLoader] = useState<string>("all");
  const [owedOnly, setOwedOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["receipts_register"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pending_receipts")
        .select(`
          receipt_id, captured_at, status, supplier_guess, amount_guess, notes,
          storage_path, mime_type, rejection_reason, payment_method, claimed_personal,
          project:projects!pending_receipts_project_id_fkey(project_code, project_name),
          captured_by_employee_id,
          capturer:employees!pending_receipts_captured_by_employee_id_fkey(first_name, last_name),
          promoted_to_ap_invoice_id,
          ap:ap_invoices!pending_receipts_promoted_to_ap_invoice_id_fkey(ap_invoice_id, supplier_name, amount_inc_gst, status, is_reimbursement, reimburse_employee_id)
        `)
        .order("captured_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      const list = (data ?? []) as any[];
      list.forEach((r) => {
        if (Array.isArray(r.capturer)) r.capturer = r.capturer[0] ?? null;
        if (Array.isArray(r.ap))       r.ap = r.ap[0] ?? null;
        if (Array.isArray(r.project))  r.project = r.project[0] ?? null;
      });
      // Reimbursement payee names (separate lookup — keeps the embed shallow).
      const eids = Array.from(
        new Set(list.map((r) => r.ap?.reimburse_employee_id).filter(Boolean)),
      ) as string[];
      if (eids.length) {
        const { data: emps } = await supabase
          .from("employees").select("employee_id, first_name, last_name").in("employee_id", eids);
        const byE = new Map(
          (emps ?? []).map((e: any) => [e.employee_id, [e.first_name, e.last_name].filter(Boolean).join(" ")]),
        );
        list.forEach((r) => {
          if (r.ap?.reimburse_employee_id) r._reimbName = byE.get(r.ap.reimburse_employee_id) ?? null;
        });
      }
      return list as Row[];
    },
  });

  const loaders = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => { if (r.captured_by_employee_id) m.set(r.captured_by_employee_id, loaderName(r)); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (loader !== "all" && r.captured_by_employee_id !== loader) return false;
      if (owedOnly) { const rb = reimb(r); if (!rb || rb.owedAmt <= 0) return false; }
      if (needle) {
        const hay = [loaderName(r), supplierOf(r), r.notes ?? "", r.rejection_reason ?? "", r.project?.project_code ?? "", r.project?.project_name ?? "", amountOf(r) != null ? String(amountOf(r)) : ""].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, status, loader, owedOnly]);

  const kpi = useMemo(() => {
    let inQueue = 0, coded = 0, rejected = 0, owed = 0;
    rows.forEach((r) => {
      if (r.status === "new") inQueue++;
      else if (r.status === "promoted") coded++;
      else if (r.status === "rejected") rejected++;
      const rb = reimb(r); if (rb) owed += rb.owedAmt;
    });
    return { total: rows.length, inQueue, coded, rejected, owed };
  }, [rows]);

  function doExport() {
    exportCsv(dated("receipts_register"), filtered, [
      { header: "Date",          value: (r: Row) => (r.captured_at ? fmtDate(r.captured_at) : "") },
      { header: "Loaded by",     value: (r: Row) => loaderName(r) },
      { header: "Supplier",      value: (r: Row) => supplierOf(r) },
      { header: "Amount (inc)",  value: (r: Row) => amountOf(r) ?? "" },
      { header: "Stage",         value: (r: Row) => stage(r).label },
      { header: "Reimbursement", value: (r: Row) => { const rb = reimb(r); return rb ? rb.label : ""; } },
    ]);
  }

  if (!user) return null;
  if (!hasRole(user, "manager")) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <EmptyState
            title="Managers and directors only"
            description="The receipts register shows every person's loaded receipts, so it's limited to managers and directors."
            icon={AlertTriangle}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto pb-16">
      <Link to="/ap" className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2">
        <ArrowLeft size={14} /> Back to AP
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ReceiptText size={22} className="text-accent" /> Receipts register
          </h1>
          <div className="text-text-muted text-[13px] mt-1">
            Every receipt the team has loaded — who loaded it, where it is in the flow, and what's owed back.
          </div>
        </div>
        <Button variant="secondary" icon={Download} onClick={doExport} disabled={!filtered.length}>
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <KPI label="Receipts"       value={String(kpi.total)} />
        <KPI label="In queue"       value={String(kpi.inQueue)} accent={kpi.inQueue > 0} />
        <KPI label="Coded"          value={String(kpi.coded)} />
        <KPI label="Rejected"       value={String(kpi.rejected)} />
        <KPI label="Reimb. owed"    value={fmtMoney(kpi.owed)} accent={kpi.owed > 0} />
      </div>

      <Card className="mb-4">
        <div className="p-3 md:p-4 grid gap-3 md:grid-cols-4">
          <div className="md:col-span-2 relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search loader, supplier, notes, decline reason, amount, job…"
              className="w-full bg-ink border border-line rounded pl-8 pr-2.5 py-1.5 text-[13px] text-text"
            />
          </div>
          <select
            value={loader}
            onChange={(e) => setLoader(e.target.value)}
            className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
          >
            <option value="all">All loaders</option>
            {loaders.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
          >
            <option value="all">All statuses</option>
            <option value="new">In queue</option>
            <option value="promoted">Coded / promoted</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="px-3 md:px-4 pb-3 -mt-1">
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-[12px] text-text-muted">
            <input type="checkbox" className="accent-accent" checked={owedOnly} onChange={(e) => setOwedOnly(e.target.checked)} />
            Reimbursements owed only
          </label>
        </div>
      </Card>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Spinner size={22} /></div>
      ) : !filtered.length ? (
        <Card><EmptyState title="No receipts match" description="Try clearing the filters." icon={ReceiptText} /></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider2 text-text-muted border-b border-line">
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-4 py-2.5 font-medium">Loaded by</th>
                  <th className="px-4 py-2.5 font-medium">Supplier</th>
                  <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                  <th className="px-4 py-2.5 font-medium">Stage</th>
                  <th className="px-4 py-2.5 font-medium">Reimbursement</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const st = stage(r);
                  const rb = reimb(r);
                  const isOpen = openId === r.receipt_id;
                  return (
                    <Fragment key={r.receipt_id}>
                    <tr className={`border-b border-line/60 hover:bg-ink-hover cursor-pointer ${isOpen ? "bg-ink-hover" : ""}`} onClick={() => setOpenId(isOpen ? null : r.receipt_id)}>
                      <td className="px-4 py-2.5 whitespace-nowrap text-text-muted">
                        <span className="inline-flex items-center gap-1.5">{isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}{r.captured_at ? fmtDate(r.captured_at) : "—"}</span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap font-medium">{loaderName(r)}</td>
                      <td className="px-4 py-2.5">{supplierOf(r)}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap text-right font-mono">{fmtMoney(amountOf(r))}</td>
                      <td className="px-4 py-2.5"><Pill label={st.label} tone={st.tone} small /></td>
                      <td className="px-4 py-2.5">{rb ? <Pill label={rb.label} tone={rb.tone} small /> : <span className="text-text-muted">—</span>}</td>
                      <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {r.status === "promoted" && r.ap ? (
                          <Link to="/ap/xero" className="text-accent hover:underline inline-flex items-center gap-1 text-[12px]">
                            AP line <ExternalLink size={11} />
                          </Link>
                        ) : r.status === "new" ? (
                          <Link to="/ap/receipts" className="text-accent hover:underline text-[12px]">Queue</Link>
                        ) : null}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-line/60 bg-ink-raised/40">
                        <td colSpan={7} className="px-4 py-3"><ReceiptDetail row={r} /></td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// ReceiptDetail — the expanded row: the file, the reason, the rest of the
// record, and "put back in queue" for a rejected one.
// -----------------------------------------------------------------------------
const PAYMENT_LABEL: Record<string, string> = {
  company_card: "Company card", personal: "Personal (reimburse)", cash: "Cash", account: "On account", other: "Other",
};

function ReceiptDetail({ row: r }: { row: Row }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isPdf = (r.mime_type ?? "").includes("pdf") || (r.storage_path ?? "").toLowerCase().endsWith(".pdf");

  useEffect(() => {
    let alive = true;
    if (!r.storage_path) return;
    supabase.storage.from("receipt-captures").createSignedUrl(r.storage_path, 3600).then(({ data }) => {
      if (alive) setUrl(data?.signedUrl ?? null);
    });
    return () => { alive = false; };
  }, [r.storage_path]);

  async function backToQueue() {
    if (!window.confirm("Put this receipt back in the AP queue? The decline reason is cleared and it will show for coding again.")) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from("pending_receipts")
        .update({ status: "new", rejection_reason: null, updated_at: new Date().toISOString() })
        .eq("receipt_id", r.receipt_id);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["receipts_register"] });
      qc.invalidateQueries({ queryKey: ["pending_receipts_queue"] });
    } catch (e: any) {
      window.alert(`Couldn't put it back: ${e?.message ?? e}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="grid gap-4 md:grid-cols-[220px_1fr]">
      <div>
        {!r.storage_path ? (
          <div className="text-[12px] text-text-muted">No file on this receipt.</div>
        ) : !url ? (
          <div className="h-40 flex items-center justify-center"><Spinner size={16} /></div>
        ) : isPdf ? (
          <a href={url} target="_blank" rel="noreferrer" className="h-40 rounded-md border border-line bg-ink flex flex-col items-center justify-center gap-1 text-[12px] text-accent hover:underline">
            <ReceiptText size={22} /> Open PDF <ExternalLink size={11} />
          </a>
        ) : (
          <a href={url} target="_blank" rel="noreferrer" title="Open full size">
            <img src={url} alt="Receipt" className="max-h-64 w-full object-contain rounded-md border border-line bg-ink" />
          </a>
        )}
      </div>
      <div className="text-[13px] space-y-2">
        {r.status === "rejected" && (
          <div className="rounded-md border border-danger/40 bg-danger/8 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider2 text-danger font-medium">Declined</div>
            <div className="mt-0.5">{r.rejection_reason || <span className="text-text-muted">no reason recorded</span>}</div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5">
          <Detail label="Supplier (OCR guess)" value={r.supplier_guess} />
          <Detail label="Amount (OCR guess)" value={r.amount_guess != null ? fmtMoney(Number(r.amount_guess)) : null} />
          <Detail label="Payment" value={r.payment_method ? (PAYMENT_LABEL[r.payment_method] ?? r.payment_method) : null} />
          <Detail label="Job" value={r.project ? `${r.project.project_code} · ${r.project.project_name}` : null} />
          <Detail label="Loaded" value={r.captured_at ? fmtDate(r.captured_at) : null} />
          <Detail label="Personal claim" value={r.claimed_personal ? "Yes" : null} />
        </div>
        {r.notes && <div><span className="text-[11px] uppercase tracking-wider2 text-text-muted">Notes</span><div>{r.notes}</div></div>}
        {r.status === "rejected" && (
          <div className="pt-1">
            <Button size="sm" variant="secondary" icon={RotateCcw} onClick={backToQueue} disabled={busy}>
              {busy ? "Putting back…" : "Put back in the AP queue"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted">{label}</div>
      <div className={value ? "" : "text-text-muted"}>{value || "—"}</div>
    </div>
  );
}
